import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Swiper
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectFade, Autoplay, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-fade";

type Banner = { image: string };
const banners: Banner[] = [{ image: "/Careers.jpg" }];

const easeOut: [number, number, number, number] = [0.16, 1, 0.3, 1];

const Careers: React.FC = () => {
  const [showImage, setShowImage] = useState(false);
  const [content, setContent] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch("/api/homepage/content");
        const data = await response.json();
        
        // Map backend data to the structure the component expects
        const careersData = data.Careers || {};
        const formattedContent = {
          hero: {
            topLine: careersData.hero_top_line,
            title: careersData.hero_title,
            desc: careersData.hero_desc,
            btnText: careersData.hero_btn_text,
            banners: careersData.hero_image ? [{ image: careersData.hero_image.startsWith('http') ? careersData.hero_image : `https://hamdanoman.com${careersData.hero_image}` }] : [{ image: "/Careers.jpg" }]
          },
          content: {
            sidebarTitle: careersData.sidebar_title,
            cvText: careersData.cv_text,
            sidebarEmail: careersData.sidebar_email,
            sidebarDesc: careersData.sidebar_desc,
            paragraphs: [
                careersData.content_p1,
                careersData.content_p2,
                careersData.content_p3,
                careersData.content_p4
            ].filter(Boolean),
            oppTitle: careersData.opp_title,
            oppDesc: careersData.opp_desc,
            oppBtnText: careersData.opp_btn_text
          },
          notice: {
            title: careersData.notice_title,
            desc: careersData.notice_desc,
            btnText: careersData.notice_btn_text,
            image: careersData.notice_image ? (careersData.notice_image.startsWith('http') ? careersData.notice_image : `https://hamdanoman.com${careersData.notice_image}`) : "/News/Recruitment Notification.jpg",
            reminderTitle: careersData.reminder_title,
            reminderDesc: careersData.reminder_desc,
            reminderBtnText: careersData.reminder_btn_text
          }
        };

        setContent(formattedContent);
      } catch (err) {
        console.error("Failed to fetch careers content", err);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const banners = content?.hero?.banners || [{ image: "/Careers.jpg" }];

  // ESC close for modal
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowImage(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="bg-white text-gray-900" >
      {/* HERO */}
      <section className="relative w-full h-screen">
        <Swiper
          modules={[EffectFade, Autoplay, Navigation]}
          effect="fade"
          loop
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          className="w-full h-full"
        >
          {(content?.hero?.banners || banners).map((b: any, i: number) => (
            <SwiperSlide key={i}>
              <div className="relative w-full h-screen">
                <img
                  src={b.image}
                  alt="Careers Banner"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/70" />

                {/* ✅ HERO TEXT (NO SVG) */}
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center px-5 text-center">
                  <p className="text-white/90 font-semibold tracking-wide text-sm sm:text-base md:text-lg">
                    {content?.hero?.topLine || "Dreaming BIG.. Find your Next Role"}
                  </p>

                  <h1 className="mt-3 text-white font-extrabold leading-tight text-2xl sm:text-4xl md:text-5xl">
                    {b.title || content?.hero?.title}
                  </h1>

                  <p className="mt-3 max-w-3xl text-white/90 leading-relaxed text-base md:text-1xl">
                    {b.subtitle || content?.hero?.desc}
                  </p>

                  <div className="mt-8 sm:mt-10">
                    <a
                      href="/job-portal"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center rounded-xl bg-indigo-500 hover:bg-indigo-600 transition shadow-lg px-10 py-4 sm:px-10 sm:py-5"
                    >
                      <span className="text-white font-bold text-lg sm:text-xl md:text-1xl">
                        {content?.hero?.btnText || "View Openings"}
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Wave */}
        <div className="absolute bottom-0 w-full overflow-hidden leading-none z-10">
          <svg
            className="w-full h-[60px] md:h-[80px]"
            viewBox="0 0 1440 80"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path fill="#ffffff" d="M0,5 C360,75 1080,75 1440,5 L1440,80 L0,80 Z" />
          </svg>
        </div>
      </section>

      {/* CONTENT */}
      <div className="max-w-7xl mx-auto px-6 py-16 md:py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* LEFT */}
          <motion.aside
            className="lg:col-span-4"
            initial={{ opacity: 0, x: -28 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: easeOut }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <div className="sticky top-24">
              <p className="inline-flex items-center gap-2 text-xs font-semibold tracking-wide text-gray-700 bg-white border border-gray-200 px-3 py-1 rounded-full">
                Careers
              </p>

              <h1 className="mt-4 text-3xl md:text-[44px] font-extrabold leading-[1.1] tracking-tight">
                {content?.content?.sidebarTitle === "Careers Without Boundaries" || !content?.content?.sidebarTitle ? (
                  <>
                    <span className="block text-[#0B1220]">Careers</span>
                    <span className="block text-[#FF0000] md:whitespace-nowrap">Without Boundaries</span>
                  </>
                ) : content?.content?.sidebarTitle?.includes("Without Boundaries") ? (
                  <>
                    <span className="block text-[#0B1220]">
                      {content.content.sidebarTitle.split("Without Boundaries")[0]}
                    </span>
                    <span className="block text-[#FF0000] md:whitespace-nowrap">Without Boundaries</span>
                  </>
                ) : (
                  <span className="text-[#0B1220]">{content?.content?.sidebarTitle}</span>
                )}
              </h1>

              <div className="mt-8 h-[5px] w-20 bg-[#FF0000]" />

              <p className="mt-6 text-sm text-gray-600 leading-relaxed">
                {content?.content?.cvText || "Send your CV as a Word Document to"}
              </p>

              <a
                href={`mailto:${content?.content?.sidebarEmail || "Careers@hamdanoman.com"}`}
                className="mt-2 inline-block text-blue-700 font-semibold hover:underline"
              >
                {content?.content?.sidebarEmail || "Careers@hamdanoman.com"}
              </a>

              <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                {content?.content?.sidebarDesc || "or browse the currently available positions and apply using our online form."}
              </p>
            </div>
          </motion.aside>

          {/* RIGHT */}
          <motion.main
            className="lg:col-span-8 space-y-10"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.4, ease: easeOut }}
            viewport={{ once: true, amount: 0.15 }}
          >
            {/* ✅ DIFFERENT TOP NOTICE STRIP */}
            <motion.div
              className="rounded-2xl border border-red-200 bg-red-50 p-5"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, ease: easeOut }}
              viewport={{ once: true, amount: 0.25 }}
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <p className="text-xs font-bold tracking-widest text-red-700 uppercase">
                    {content?.notice?.title || "Recruitment Notice"}
                  </p>
                  <p className="mt-1 text-sm text-gray-700 leading-relaxed">
                    {content?.notice?.desc?.includes("info@hamdanoman.com") ? (
                      <>
                        {content.notice.desc.split("info@hamdanoman.com")[0]}
                        <a href="mailto:info@hamdanoman.com" className="text-blue-700 font-semibold hover:underline">
                          info@hamdanoman.com
                        </a>
                        {content.notice.desc.split("info@hamdanoman.com")[1]}
                      </>
                    ) : (
                      content?.notice?.desc || (
                        <>
                          For recruitment verification or suspicious offers, contact{" "}
                          <a href="mailto:info@hamdanoman.com" className="text-blue-700 font-semibold hover:underline">
                            info@hamdanoman.com
                          </a>
                        </>
                      )
                    )}
                  </p>
                </div>

                <button
                  onClick={() => setShowImage(true)}
                  className="inline-flex justify-center rounded-xl px-5 py-2 bg-red-600 text-white font-semibold hover:bg-red-700 transition"
                >
                  {content?.notice?.btnText || "View Notice"}
                </button>
              </div>
            </motion.div>

            {/* ✅ IMAGE + CAPTION SPLIT (NEW STYLE) */}
            <motion.div
              className="grid md:grid-cols-12 gap-6 items-start"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: easeOut }}
              viewport={{ once: true, amount: 0.25 }}
            >
              <div className="md:col-span-8 rounded-2xl overflow-hidden border border-gray-200 bg-white shadow-sm">
                <img
                  src={content?.notice?.image || "/News/Recruitment Notification.jpg"}
                  alt="Recruitment Notification"
                  onClick={() => setShowImage(true)}
                  className="w-full max-h-[380px] object-contain cursor-pointer hover:opacity-90 transition"
                />
              </div>

              <div className="md:col-span-4 rounded-2xl border border-gray-200 bg-gray-50 p-5">
                <p className="text-sm font-bold text-[#0B1220]">
                  {content?.notice?.reminderTitle || "Important Reminder"}
                </p>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                  {content?.notice?.reminderDesc || "Hamdan Trading Group does not request payments for recruitment. Always verify messages and emails before responding."}
                </p>

                <button
                  onClick={() => setShowImage(true)}
                  className="mt-4 text-sm font-semibold text-blue-700 hover:underline"
                >
                  {content?.notice?.reminderBtnText || "Open full image →"}
                </button>
              </div>
            </motion.div>

            {/* TEXT CONTENT (animated on scroll) */}
            {(content?.content?.paragraphs || [
              { text: "At Hamdan, our employees are our assets and our brand ambassadors. We are proud to have the cream of the lot amongst us and look up towards them to help us create history. A team that is highly dedicated, creative, enthusiastic, energetic, flexible, challenging and customer-oriented with excellent relationship management skills – truly a delight and an inspiration to be around!" },
              { text: "Hamdan is built upon strong convictions and fair business principles and is hence, ideally positioned to leverage its strengths making optimum use of resources thus leading onto to becoming one of the most sought after business houses in the region. A career at Hamdan provides a world-class business environment where employees gain invaluable experience and become part of an unprecedented company." },
              { text: "Our employees are the passion behind everything we do. Their vigour, focus and enthusiasm help us change the landscape of Oman, create landmark projects. We value our staff at Hamdan and ensure that we employ the very best, who strive to work with a dynamic and rapidly growing organization. We are also committed to recruiting Oman Nationals as well as supporting the development and growth of fresh graduates into rewarding roles." },
              { text: "We value diversity and multi-ethnicity and employ numerous nationalities all working together in the pursuit of our corporate goals." },
            ]).map((t: any, i: number) => (
              <motion.p
                key={i}
                className="text-gray-700 text-justify text-[15px] sm:text-sm md:text-base leading-[1.5]"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: i * 0.06, ease: easeOut }}
                viewport={{ once: true, amount: 0.25 }}
              >
                {t.text || t}
              </motion.p>
            ))}

            {/* OPPORTUNITIES */}
            <motion.div
              className=" pt-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: easeOut }}
              viewport={{ once: true, amount: 0.25 }}
            >
              <h2 className="text-2xl font-bold mb-4">{content?.content?.oppTitle || "Opportunities"}</h2>
              <p className="text-gray-700 leading-[1.5]">
                {content?.content?.oppDesc || "From sales to marketing and engineering to accounts, there is a plethora of opportunities at Hamdan to help you reach your goal. We encourage applications from Oman nationals and Oman residents, as well as individuals located outside the Oman."}
              </p>

              <a
                href={`mailto:${content?.content?.sidebarEmail || "Careers@hamdanoman.com"}`}
                className="inline-block mt-6 px-6 py-3 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 transition"
              >
                {content?.content?.oppBtnText || "Apply via Email"}
              </a>
            </motion.div>
          </motion.main>
        </div>
      </div>

      {/* MODAL */}
      <AnimatePresence>
        {showImage && (
          <motion.div
            className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center px-4"
            onClick={() => setShowImage(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <motion.div
              className="relative max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.96, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97, y: 10 }}
              transition={{ duration: 0.25, ease: easeOut }}
            >
              <button
                onClick={() => setShowImage(false)}
                className="absolute -top-4 -right-4 w-10 h-10 rounded-full bg-black text-white text-xl flex items-center justify-center hover:bg-gray-800 transition"
                aria-label="Close"
              >
                ✕
              </button>

              <img
                src={content?.notice?.image || "/News/Recruitment Notification.jpg"}
                alt="Recruitment Notification Full View"
                className="w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Careers;
